import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '../../../components/PageContainer';
import { Button } from '../../../components/Button';

const LiveStreamDemo = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchStreamUrl = async () => {
      try {
        const response = await fetch(
          'https://nk6jtwgw3l.execute-api.eu-west-2.amazonaws.com/farmstream/internal/livestream-url?camera_id=89462038043017661002'
        );
        
        if (!response.ok) {
          throw new Error('Failed to load stream URL');
        }
        
        const url = await response.text();
        
        if (videoRef.current) {
          videoRef.current.src = url;
          setIsLoading(false);
        }
      } catch (err) {
        console.error(err);
        setError('Could not load the livestream.');
        setIsLoading(false);
      }
    };

    fetchStreamUrl();
  }, []);

  return (
    <VideoWrapper>
      <StyledVideo
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        style={{ display: isLoading || error ? 'none' : 'block' }}
      />
      
      {(isLoading || error) && (
        <LoadingContainer>
          {isLoading && <Spinner />}
          <LoadingText>
            {error || 'Loading livestream...'}
          </LoadingText>
        </LoadingContainer>
      )}
    </VideoWrapper>
  );
};

export const Livestreaming = () => {
  const navigate = useNavigate();

  const handleSetupClick = () => {
    navigate('/livestreaming/setup');
  };

  return (
      <Container>
        <Header>
          <Title>Livestream Your 4G Camera to the World</Title>
          <Subtitle>
            We're excited to announce that all existing 4G cameras using a Farmstream SIM can now livestream directly to your website.
          </Subtitle>
        </Header>

        <CompatibilityNotice>
          <CompatibilityTitle>📷 Compatible Cameras</CompatibilityTitle>
          <CompatibilityText>
            Livestreaming is available for <strong>Farmstream Static 4G</strong> and <strong>Farmstream 360 4G</strong> cameras only.
          </CompatibilityText>
          <IncompatibleText>
            ⚠️ The <strong>Solar Mini</strong> does not support livestreaming due to its low-power solar design.
          </IncompatibleText>
        </CompatibilityNotice>

        <Section>

          <FeatureList>
            <FeatureItem>• Showcase your business or project</FeatureItem>
            <FeatureItem>• Build trust with customers</FeatureItem>
            <FeatureItem>• Share what makes your work unique</FeatureItem>
          </FeatureList>
        </Section>

        <Section>
          <SectionTitle>💡 How it works:</SectionTitle>
          <Description>
            The livestream runs on a 30-second loop every hour by default — but we can
            easily configure custom durations, times of day, or specific days of the week to suit
            your needs.
          </Description>
        </Section>


        <Section>
          <SectionTitle>📋 Requirements:</SectionTitle>
          <Description>
            The only requirement to get started is a <strong>Farmstream Heavy Streaming plan</strong>. This ensure you have the necessary data allowance for continuous streaming and cover the aditional costs of the livestream service.

          </Description>
        </Section>

        <Section>
          <SectionTitle>🔧 Easy Integration:</SectionTitle>
          <Description>
            Once set up, we provide you with simple HTML code that you or your website editor can easily add to any page on your website. No technical expertise required — just copy, paste, and your livestream is live!
          </Description>
        </Section>

 
        <Section>
          <SectionTitle>🎯 Perfect for:</SectionTitle>
          <FeatureList>
            <FeatureItem>• Construction sites documenting project progress and keeping stakeholders updated remotely</FeatureItem>
            <FeatureItem>• Farms showcasing livestock welfare and crop practices for transparency-conscious consumers</FeatureItem>
            <FeatureItem>• Event venues displaying facilities in real-time to attract bookings and showcase atmosphere</FeatureItem>
            <FeatureItem>• Tourism destinations showing current conditions and natural beauty to attract visitors</FeatureItem>
            <FeatureItem>• Business premises building customer confidence by showcasing operations and activity</FeatureItem>
            <FeatureItem>• Environmental projects tracking wildlife, weather, or conservation for public engagement</FeatureItem>
          </FeatureList>
        </Section>



        <ExampleSection>
          <ExampleTitle>Live Example</ExampleTitle>
          <ExampleText>
            This camera is configured to livestream on a loop every hour between 9:00 AM and
            5:00 PM, Monday to Friday. Here's a live demonstration:
          </ExampleText>
          <LiveStreamDemo />
        </ExampleSection>



        <Section>
          <SectionTitle>Setup Video Guide:</SectionTitle>
          <Description>
            Watch our step-by-step guide to setting up your livestream to your website. Get in touch if you need any help with the setup:
          </Description>
          <VideoSection>
            <iframe
              width="100%"
              height="400"
              src="https://www.youtube.com/embed/0VJILdGrgXU?si=RHApPRd3JS-az2oD"
              title="Livestream Setup Guide"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            />
          </VideoSection>
        </Section>



        <NewFeatureSection>
          <NewFeatureBadge>NEW</NewFeatureBadge>
          <SectionTitle>📸 Still Image Updates</SectionTitle>
          <Description>
            We're now able to provide <strong>still image updates</strong> from your camera that refresh every 15 minutes — perfect for embedding a live snapshot on your website without the data requirements of full video streaming.
          </Description>
          <Description style={{ marginTop: '0.75rem' }}>
            This feature is currently being rolled out. If you're interested, please get in touch at <strong>support@farmstream.co.uk</strong> and we'll get you set up.
          </Description>
        </NewFeatureSection>

        <ContactSection>
          <ContactTitle>Ready to Get Started?</ContactTitle>

          <SetupButton onClick={handleSetupClick}>Set Up Livestream</SetupButton>
        </ContactSection>

      </Container>
  );
};

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  line-height: 1.6;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
`;

const CompatibilityNotice = styled.div`
  background-color: #f0f7ff;
  border: 1px solid #b3d4fc;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2.5rem;
`;

const CompatibilityTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 0.75rem;
`;

const CompatibilityText = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  margin: 0 0 0.5rem 0;
`;

const IncompatibleText = styled.p`
  font-size: 0.9rem;
  color: #b45309;
  margin: 0;
`;

const Section = styled.div`
  margin-bottom: 2.5rem;
`;

const NewFeatureSection = styled.div`
  background-color: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 2.5rem;
  position: relative;
`;

const NewFeatureBadge = styled.span`
  background-color: #16a34a;
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  border-radius: 0.25rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
  letter-spacing: 0.05em;
`;

const ContactLink = styled.a`
  display: inline-block;
  margin-top: 1rem;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FeatureItem = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
`;

const Description = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  margin: 0;
`;

const CallToAction = styled.p`
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-top: 1rem;
  margin-bottom: 0;
`;

const ExampleSection = styled.div`
  background-color: ${({ theme }) => theme.colors.gray};
  padding: 2rem;
  border-radius: 0.5rem;
  margin: 2.5rem 0;
`;

const ExampleTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1rem;
`;

const ExampleText = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.black};
  margin-bottom: 1.5rem;
`;

const ContactSection = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  padding: 2.5rem;
  border-radius: 0.5rem;
  text-align: center;
`;

const ContactTitle = styled.h3`
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
`;

const ContactDescription = styled.p`
  font-size: 0.9rem;
  margin-bottom: 2rem;
`;

const SetupButton = styled.button`
  background-color: white;
  color: ${({ theme }) => theme.colors.primary};
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 0.5rem;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray};
    transform: translateY(-2px);
  }
`;

// LiveStream Demo Components
const VideoWrapper = styled.div`
  width: 100%;
  max-width: 720px;
  background-color: #000;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
  margin: 0 auto;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: auto;
  display: block;
  border: none;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const Spinner = styled.div`
  border: 4px solid #f3f3f3;
  border-top: 4px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin-top: 20px;
`;

const LoadingText = styled.div`
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.gray900};
  margin-top: 8px;
  text-align: center;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  padding: 3rem;
`;

const VideoSection = styled.div`
  margin: 1.5rem 0;
  
  iframe {
    border-radius: 0.5rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    iframe {
      height: 250px;
    }
  }
`;
